import { useMemo, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  ChevronRight as ChevronRightIcon,
  ExpandMore as ExpandMoreIcon,
} from "@mui/icons-material";

import type {
  DeferredLinesBySupplier,
  EnrichedDeferredLine,
} from "../../services/distributionPreviewService";
import { DebouncedSearchField } from "../DebouncedSearchField";
import { filterBucketsByQuery } from "./deferred-filter.helper";
import { DeferredSupplierLinesTable } from "./DeferredSupplierLinesTable";

/**
 * Auditoría 2026-05-05 — panel del dialog de Revisión previa que muestra
 * las líneas diferidas (por capacity cap) agrupadas por proveedor en
 * lugar de por categoría. Almacén/logística operan por proveedor: cuando
 * llega mercancía a la bodega viene de un proveedor, se acomoda por
 * proveedor, los camiones se arman por proveedor.
 *
 * El operador puede:
 *   - Marcar el checkbox del proveedor → selecciona TODAS sus líneas
 *     hijas con stock>0.
 *   - Expandir el proveedor → ve productos individuales con
 *     `stock_en_origen` visible y selecciona productos específicos.
 *   - Click "Re-promover seleccionados" → llama al endpoint del backend.
 *
 * Override implícito (sin warning bloqueante): el operador que revisa
 * tiene autoridad para decidir; el cap es referencia, no hard limit.
 */
export interface DeferredBySupplierPanelProps {
  buckets: DeferredLinesBySupplier[];
  isLoading?: boolean;
  onRepromote: (selectedIds: string[]) => Promise<void>;
}

export function DeferredBySupplierPanel({
  buckets,
  isLoading = false,
  onRepromote,
}: DeferredBySupplierPanelProps) {
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [supplierQuery, setSupplierQuery] = useState("");
  const [productQuery, setProductQuery] = useState("");

  const filteredBuckets = useMemo(
    () => filterBucketsByQuery(buckets, supplierQuery, productQuery),
    [buckets, supplierQuery, productQuery],
  );

  const totals = useMemo(() => {
    const lines = filteredBuckets.reduce((acc, b) => acc + b.total_lines, 0);
    const units = filteredBuckets.reduce((acc, b) => acc + b.total_units, 0);
    return { lines, units };
  }, [filteredBuckets]);

  const totalBuckets = buckets.length;
  const isFiltered =
    supplierQuery.trim().length > 0 || productQuery.trim().length > 0;

  if (buckets.length === 0) {
    return (
      <Paper
        variant="outlined"
        sx={{
          textAlign: "center",
          py: 4,
          px: 2,
          bgcolor: "grey.50",
          color: "text.secondary",
        }}
      >
        <Typography variant="body2">
          Sin líneas diferidas — el cap de capacidad cubre toda la demanda
          recomendada para esta tienda.
        </Typography>
      </Paper>
    );
  }

  const toggleBucket = (bucket: DeferredLinesBySupplier) => {
    const next = new Set(selected);
    const eligibleIds = bucket.lines
      .filter((l) => l.stock_en_origen > 0)
      .map((l) => l.recommendation_id);
    const allChecked = eligibleIds.every((id) => next.has(id));
    if (allChecked) {
      eligibleIds.forEach((id) => next.delete(id));
    } else {
      eligibleIds.forEach((id) => next.add(id));
    }
    setSelected(next);
  };

  const toggleLine = (line: EnrichedDeferredLine) => {
    if (line.stock_en_origen <= 0) return;
    const next = new Set(selected);
    if (next.has(line.recommendation_id)) {
      next.delete(line.recommendation_id);
    } else {
      next.add(line.recommendation_id);
    }
    setSelected(next);
  };

  const toggleExpand = (key: string) => {
    const next = new Set(expanded);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    setExpanded(next);
  };

  const handleRepromote = async () => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    await onRepromote(ids);
    setSelected(new Set());
  };

  return (
    <Paper variant="outlined" sx={{ overflow: "hidden" }}>
      {/* Header del panel */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={2}
        sx={{
          px: 2.5,
          py: 1.5,
          bgcolor: "grey.100",
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        <Box>
          <Typography variant="subtitle2" fontWeight={600}>
            Líneas diferidas por capacidad
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {totals.lines.toLocaleString("es-MX")} líneas ·{" "}
            <Box component="span" sx={{ fontWeight: 600 }}>
              {totals.units.toLocaleString("es-MX")} unidades
            </Box>{" "}
            · {filteredBuckets.length} proveedor
            {filteredBuckets.length === 1 ? "" : "es"}
            {isFiltered && (
              <Box
                component="span"
                sx={{ ml: 1, color: "warning.dark", fontWeight: 600 }}
              >
                (filtrado de {totalBuckets})
              </Box>
            )}
          </Typography>
        </Box>
        <Button
          variant="contained"
          size="small"
          onClick={handleRepromote}
          disabled={selected.size === 0 || isLoading}
          aria-label="Re-promover seleccionados"
          sx={{ whiteSpace: "nowrap" }}
        >
          Re-promover
          <Chip
            label={selected.size}
            size="small"
            sx={{
              ml: 1,
              bgcolor: "primary.dark",
              color: "primary.contrastText",
              height: 20,
              fontSize: "0.7rem",
              fontWeight: 600,
            }}
          />
        </Button>
      </Stack>

      {/* Filtros: proveedor (marca) y producto. Sintaxis: coma=OR, prefijo - excluye. */}
      <Stack
        direction="row"
        spacing={1}
        sx={{
          px: 2.5,
          py: 1,
          bgcolor: "background.paper",
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        <DebouncedSearchField
          placeholder="Proveedor: yuliana, tramontina (OR)"
          onDebouncedChange={setSupplierQuery}
          sx={{ flex: 1 }}
        />
        <DebouncedSearchField
          placeholder="Producto: vaso, -termo (excluye)"
          onDebouncedChange={setProductQuery}
          sx={{ flex: 1 }}
        />
      </Stack>

      {/* Lista de buckets de proveedor */}
      <Box sx={{ maxHeight: "60vh", overflowY: "auto" }}>
        {filteredBuckets.length === 0 && (
          <Box sx={{ p: 3, textAlign: "center", color: "text.secondary" }}>
            <Typography variant="body2">
              Ningún proveedor coincide con los filtros aplicados.
            </Typography>
          </Box>
        )}
        {filteredBuckets.map((bucket, idx) => {
          const key = bucket.supplier_name ?? "_null";
          const eligibleIds = bucket.lines
            .filter((l) => l.stock_en_origen > 0)
            .map((l) => l.recommendation_id);
          const allChecked =
            eligibleIds.length > 0 &&
            eligibleIds.every((id) => selected.has(id));
          const someChecked =
            !allChecked && eligibleIds.some((id) => selected.has(id));
          const isExpanded = expanded.has(key);
          const noEligible = eligibleIds.length === 0;
          return (
            <Box
              key={key}
              sx={{
                borderBottom: idx === filteredBuckets.length - 1 ? 0 : 1,
                borderColor: "divider",
              }}
            >
              <SupplierRow
                bucket={bucket}
                allChecked={allChecked}
                someChecked={someChecked}
                isExpanded={isExpanded}
                noEligible={noEligible}
                onToggleBucket={() => toggleBucket(bucket)}
                onToggleExpand={() => toggleExpand(key)}
              />
              {isExpanded && (
                <DeferredSupplierLinesTable
                  lines={bucket.lines}
                  selected={selected}
                  onToggleLine={toggleLine}
                />
              )}
            </Box>
          );
        })}
      </Box>
    </Paper>
  );
}

interface SupplierRowProps {
  bucket: DeferredLinesBySupplier;
  allChecked: boolean;
  someChecked: boolean;
  isExpanded: boolean;
  noEligible: boolean;
  onToggleBucket: () => void;
  onToggleExpand: () => void;
}

function SupplierRow({
  bucket,
  allChecked,
  someChecked,
  isExpanded,
  noEligible,
  onToggleBucket,
  onToggleExpand,
}: SupplierRowProps) {
  const supplierLabel = bucket.supplier_name ?? "Sin proveedor asignado";
  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1}
      sx={{
        px: 2.5,
        py: 1,
        bgcolor: isExpanded ? "action.hover" : "transparent",
        "&:hover": { bgcolor: "action.hover" },
        transition: "background-color 150ms",
      }}
    >
      <Checkbox
        size="small"
        checked={allChecked}
        indeterminate={someChecked}
        onChange={onToggleBucket}
        disabled={noEligible}
        inputProps={{
          "aria-label": `Seleccionar todo: ${supplierLabel}`,
        }}
        sx={{ p: 0.5 }}
      />
      <IconButton
        size="small"
        onClick={onToggleExpand}
        aria-expanded={isExpanded}
        aria-label={isExpanded ? "Colapsar" : "Expandir"}
        sx={{ p: 0.5 }}
      >
        {isExpanded ? (
          <ExpandMoreIcon fontSize="small" />
        ) : (
          <ChevronRightIcon fontSize="small" />
        )}
      </IconButton>
      <Tooltip title={supplierLabel} placement="top">
        <Typography
          variant="body2"
          fontWeight={500}
          sx={{
            flex: 1,
            minWidth: 0,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            color: bucket.supplier_name ? "text.primary" : "text.secondary",
            fontStyle: bucket.supplier_name ? "normal" : "italic",
            cursor: "pointer",
          }}
          onClick={onToggleExpand}
        >
          {supplierLabel}
        </Typography>
      </Tooltip>
      <Stack direction="row" spacing={1} alignItems="center" flexShrink={0}>
        <Chip
          label={`${bucket.total_lines.toLocaleString("es-MX")} líneas`}
          size="small"
          variant="outlined"
          sx={{
            fontSize: "0.7rem",
            height: 22,
            fontVariantNumeric: "tabular-nums",
          }}
        />
        <Chip
          label={`${bucket.total_units.toLocaleString("es-MX")} u`}
          size="small"
          color="warning"
          variant="filled"
          sx={{
            fontSize: "0.7rem",
            height: 22,
            fontWeight: 600,
            fontVariantNumeric: "tabular-nums",
          }}
        />
      </Stack>
    </Stack>
  );
}

// SupplierLines extraído a `DeferredSupplierLinesTable.tsx` por
// responsabilidad y para respetar P7 (≤500 L). Auditoría 2026-05-05.
